import { COVERAGE_REGIONS } from "@/lib/content";

/**
 * Stylized SVG map of the New Jersey + Pennsylvania coverage footprint.
 * Illustrative, not geographically precise.
 */
export default function CoverageMap({ compact = false }) {
  return (
    <div className="w-full">
      <div
        className={`relative w-full bg-[#0B192C] border border-[#0B192C] overflow-hidden ${
          compact ? "aspect-[16/9]" : "aspect-[16/10]"
        }`}
        data-testid="coverage-map-svg"
      >
        <svg
          viewBox="0 0 800 500"
          className="absolute inset-0 w-full h-full"
          role="img"
          aria-label="Service coverage regions map — New Jersey and Pennsylvania"
        >
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="800" height="500" fill="url(#grid)" />

          {/* Pennsylvania — wide horizontal block */}
          <g>
            <path
              d="M 120 190
                 L 540 170
                 L 570 210
                 L 555 315
                 L 145 320
                 Z"
              fill="#1E40AF"
              stroke="#3A6D8C"
              strokeWidth="1.5"
              opacity="0.92"
            />
          </g>

          {/* New Jersey — narrow vertical block to the east */}
          <g>
            <path
              d="M 570 210
                 L 640 205
                 L 660 300
                 L 615 375
                 L 555 315
                 Z"
              fill="#1E40AF"
              stroke="#3A6D8C"
              strokeWidth="1.5"
              opacity="0.92"
            />
          </g>

          {/* State labels */}
          <g fill="#F8FAFC" fontFamily="Manrope, sans-serif" fontWeight="700">
            <text x="330" y="260" textAnchor="middle" fontSize="42">PA</text>
            <text x="610" y="295" textAnchor="middle" fontSize="26">NJ</text>
          </g>

          <g fill="#F8FAFC" fontFamily="Manrope, sans-serif" fontSize="11" opacity="0.85">
            <text x="330" y="285" textAnchor="middle">Pennsylvania</text>
            <text x="610" y="315" textAnchor="middle">New Jersey</text>
          </g>

          {/* Major city hubs */}
          <g>
            {[
              { cx: 205, cy: 275, name: "Pittsburgh" },
              { cx: 495, cy: 285, name: "Philadelphia" },
              { cx: 400, cy: 235, name: "Harrisburg" },
              { cx: 155, cy: 210, name: "Erie" },
              { cx: 613, cy: 250, name: "Newark" },
              { cx: 620, cy: 320, name: "Trenton" },
            ].map((c, i) => (
              <g key={i}>
                <circle cx={c.cx} cy={c.cy} r="10" fill="#F8FAFC" opacity="0.14" />
                <circle cx={c.cx} cy={c.cy} r="3.5" fill="#F8FAFC" />
                <text
                  x={c.cx}
                  y={c.cy - 14}
                  textAnchor="middle"
                  fill="rgba(248,250,252,0.75)"
                  fontFamily="JetBrains Mono, monospace"
                  fontSize="9"
                >
                  {c.name.toUpperCase()}
                </text>
              </g>
            ))}
          </g>

          {/* Corner labels */}
          <g fontFamily="JetBrains Mono, monospace" fontSize="10" fill="rgba(248,250,252,0.5)">
            <text x="24" y="36">MEAD_COVERAGE · V.2026</text>
            <text x="24" y="480">2 STATES · 88 COUNTIES</text>
            <text x="776" y="480" textAnchor="end">
              CREW HUBS: 6
            </text>
          </g>
        </svg>
      </div>

      {!compact && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {COVERAGE_REGIONS.map((r) => (
            <div key={r.code} className="bg-white p-6">
              <div className="flex items-baseline justify-between">
                <div className="font-display text-2xl tracking-tight">{r.name}</div>
                <div className="font-mono-alt text-[11px] text-slate-500">{r.code}</div>
              </div>
              <div className="mt-2 text-xs text-slate-500">{r.counties} counties covered</div>
              <div className="mt-2 text-sm text-slate-700 leading-relaxed">{r.cities}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
